import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import WarningNoticeBanner from '../../../../../../components/designe/warning-notice-banner/WarningNoticeBanner';
import UserMultiSelectDialog from '../../../../../../components/reusable/user-multi-select-dialog/UserMultiSelectDialog';
import { useShareTrainingUsers } from '../../../../../../redux/api/trainingsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../../../../redux/api/users/usersApi';
import { ITraining } from '../../../../../../types/interfaces/training';
import { accessRowToIUser } from '../../../../../users/settings/access-settings/access-settings-helpers';

const emptySelectedUserIds: string[] = [];

interface IShareTrainingProps {
  open: boolean;
  training: ITraining;
  onClose: () => void;
  onShared?: () => void;
}

export default function ShareTraining(props: IShareTrainingProps) {
  const { open, training, onClose, onShared } = props;
  const { t } = useTranslation();
  const { data: user } = useGetMyDetails();
  const {
    data: userTrainingAccess,
    isLoading: isAccessLoading,
    isFetching: isAccessFetching,
  } = useGetUserTrainingAccessIncoming();
  const [shareTrainingUsers, { isLoading }] = useShareTrainingUsers();

  const shareTrainingAllowedUsers = useMemo(
    () =>
      (userTrainingAccess?.shareTrainings ?? []).map((row) =>
        accessRowToIUser(row),
      ),
    [userTrainingAccess?.shareTrainings],
  );

  const alreadySharedIds = useMemo(
    () => new Set((training.availableToUsers ?? []).map((u) => u.id)),
    [training.availableToUsers],
  );

  const usersToPickFrom = useMemo(() => {
    if (!shareTrainingAllowedUsers.length) {
      return [];
    }
    return shareTrainingAllowedUsers.filter(
      (s) => s.id !== user?.id && !alreadySharedIds.has(s.id),
    );
  }, [shareTrainingAllowedUsers, user?.id, alreadySharedIds]);
  const showEmptyStateWarning =
    !isAccessLoading && !isAccessFetching && usersToPickFrom.length === 0;

  const handleSave = async (add: string[]) => {
    if (!add.length) {
      return;
    }
    try {
      await shareTrainingUsers({
        trainingId: training.id,
        add,
        remove: [],
      }).unwrap();
      onShared?.();
      onClose();
    } catch {
      // Error toast from RTK transform
    }
  };

  return (
    <UserMultiSelectDialog
      open={open}
      onClose={onClose}
      title={t('trainings.shareTraining')}
      users={usersToPickFrom}
      selectedUserIds={emptySelectedUserIds}
      onSave={handleSave}
      isSaving={isLoading}
      requireSelection
      emptyStateNotice={
        showEmptyStateWarning ? (
          <WarningNoticeBanner title={t('trainings.shareEmptyBannerTitle')}>
            {t('trainings.shareEmptyBannerDescription')}
          </WarningNoticeBanner>
        ) : null
      }
    />
  );
}
